import { styled } from '@mui/system';
import React, { useMemo, useCallback } from 'react';
import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  SvgIconProps,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowDown from '../../../../icons/svg/arrow_down.svg?react';
import { Input } from '../inputField';
import { InputFieldLabel } from '../shared/fieldLabel';
import { Checkbox } from '../checkbox';

export type SelectOption = {
  value: string | number | boolean;
  text: string;
  icon?: JSX.Element;
  group?: string;
  hidden?: boolean;
  disabled?: boolean;
};

type Props<T = unknown> = MuiSelectProps<T> & {
  options: Array<SelectOption>;
  emptyOption?: boolean;
  emptyOptionText?: string;
  helperText?: string;
  small?: boolean;
  futureEditingLocked?: boolean;
  missing?: boolean;
  missingMessage?: string;
  isMultiple?: boolean;
  tooltip?: string;
  showTooltipOnDisabled?: boolean;
};

export type SelectProps = Props;

export function Select<T = unknown>(props: Props<T>): JSX.Element {
  const CustomIconComponent = useCallback(
    (iconProps: SvgIconProps) => (
      <ArrowDown
        {...iconProps}
        className={`${iconProps.className} cursor-pointer mr-1.5 h-6 w-6 top-3 text-primary-main`}
      />
    ),
    []
  );

  const {
    options,
    helperText,
    emptyOptionText,
    emptyOption,
    placeholder,
    small,
    futureEditingLocked,
    missing,
    missingMessage,
    isMultiple,
    tooltip,
    showTooltipOnDisabled,
    MenuProps,
    ...selectProps
  } = props;
  const { t } = useTranslation('template');
  selectProps.value = selectProps.value ?? '';

  const mergedMenuProps = useMemo(
    () => ({
      ...MenuProps,
      PaperProps: {
        ...MenuProps?.PaperProps,
        className: 'max-w-[300px] max-h-[400px] overflow-y-auto rounded-lg shadow-500'
      },
      BackdropProps: {
        ...MenuProps?.BackdropProps,
        invisible: true,
        style: { backgroundColor: 'transparent', opacity: '0' }
      },
      MenuListProps: {
        ...MenuProps?.MenuListProps,
        sx: { py: 0 }
      }
    }),
    [MenuProps]
  );

  const allOptionsHaveGroup = options.every((option) => option.group);

  const isOptionSelected = (optionValue: string | number | boolean): boolean => {
    if (!isMultiple || !Array.isArray(selectProps.value)) return false;
    return selectProps.value.includes(optionValue);
  };

  const groupedOptions: { [group: string]: SelectOption[] } = {};
  options.forEach((option) => {
    if (option.group) {
      if (!groupedOptions[option.group]) {
        groupedOptions[option.group] = [];
      }
      groupedOptions[option.group].push(option);
    }
  });

  return (
    <FormControl
      fullWidth={true}
      error={props.error}
      sx={{
        '&:hover svg': {
          color: selectProps.disabled ? 'grey.300' : 'grey.900'
        }
      }}
    >
      {props.label && (
        <InputFieldLabel
          label={props.label}
          required={selectProps.required}
          futureEditingLocked={futureEditingLocked}
          missing={missing}
          missingMessage={missingMessage}
          disabled={selectProps.disabled}
          tooltip={tooltip}
          showTooltipOnDisabled={showTooltipOnDisabled}
        />
      )}
      <MuiSelect
        {...selectProps}
        IconComponent={CustomIconComponent}
        displayEmpty={(emptyOption && !!emptyOptionText) || !!placeholder}
        input={<Input small={small} />}
        multiple={isMultiple}
        MenuProps={mergedMenuProps}
        sx={{ height: small ? '2.25rem' : '3rem' }}
        renderValue={(selected) => {
          if (isMultiple && Array.isArray(selected)) {
            if (selected.length === 0) {
              return <span className="text-grey-500 text-base">{placeholder}</span>;
            }

            const displayText = selected
              .map((value) => {
                const option = options.find((opt) => opt.value === value);
                return option?.text ?? String(value);
              })
              .join(', ');

            return <span className="text-base truncate block">{displayText}</span>;
          }

          if (
            (selected === undefined || selected === null || selected === '') &&
            emptyOption &&
            emptyOptionText
          ) {
            return <Typography>{emptyOptionText}</Typography>;
          }

          if (selected === undefined || selected === null || selected === '') {
            return <Typography className="text-grey-500">{placeholder}</Typography>;
          }

          const selectedOption = options.find(
            (opt) => opt.value === selected || String(opt.value) === String(selected)
          );

          if (selectedOption?.icon) {
            return <>{selectedOption.icon}</>;
          }

          return selectedOption?.text ?? String(selected);
        }}
      >
        {emptyOption && (
          <StyledMenuItem value={''}>
            {emptyOptionText ? emptyOptionText : t('EMPTY_OPTION')}
          </StyledMenuItem>
        )}
        {allOptionsHaveGroup
          ? Object.entries(groupedOptions).map(([group, optionsInGroup]) => [
              <StyledMenuItem key={group} disabled>
                {group}
              </StyledMenuItem>,
              ...optionsInGroup.map((option) => (
                <StyledMenuItem
                  key={`${option.icon ?? option.text}-${option.value}`}
                  value={typeof option.value === 'boolean' ? String(option.value) : option.value}
                >
                  {isMultiple && <Checkbox checked={isOptionSelected(option.value)} />}
                  <div className={isMultiple ? 'ml-1' : ''}>{option.icon ?? option.text}</div>
                </StyledMenuItem>
              ))
            ])
          : options.map((option) => (
              <StyledMenuItem
                key={`${option.icon ?? option.text}-${option.value}`}
                value={typeof option.value === 'boolean' ? String(option.value) : option.value}
                hidden={option.hidden}
                disabled={option.disabled}
              >
                {isMultiple && <Checkbox checked={isOptionSelected(option.value)} />}
                <div className={isMultiple ? 'ml-1' : ''}>{option.icon ?? option.text}</div>
              </StyledMenuItem>
            ))}
      </MuiSelect>
      <FormHelperText className="text-grey-600">{helperText}</FormHelperText>
    </FormControl>
  );
}

const StyledMenuItem = styled(MenuItem)({
  height: '48px',
  '&:hover': {
    backgroundColor: 'grey.500'
  },
  '&.Mui-selected': {
    backgroundColor: 'grey.500'
  }
});
